package com.arena_web.ui.components.overlay

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import com.arena_web.ui.theme.AppGradients
import com.arena_web.ui.utils.m
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.Element
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.Event

/**
 * Marks an element inside a [BottomSheet] as a drag-to-dismiss zone when the
 * sheet was opened with `dragHeaderOnly = true`. Put it on the sheet's header
 * row; the built-in handle pill carries it automatically.
 */
const val SHEET_DRAG_AREA_CLASS = "sheet-drag-area"

/**
 * Mobile bottom sheet — the phone-sized counterpart to the desktop popup. A
 * dimmed scrim fills the screen; the panel rises from the bottom edge with a
 * drag handle, rounded top corners and a `Border/gradient/01`-tinted hairline.
 *
 * Dismissal is animated, never abrupt:
 *  - tapping the scrim slides the panel back down,
 *  - dragging the panel down past a threshold (or flicking) closes it; a short
 *    drag springs back.
 * The drag only engages the sheet when no scrollable child is scrolled away
 * from its top, so swiping a list inside the sheet scrolls the list as usual
 * and only a pull from the top edge moves the sheet. [onDismiss] fires once the
 * close animation finishes (the caller then unmounts via `if (open) { … }`).
 *
 * The panel sets `--scale: var(--mobile-scale)`, so content drawn with the
 * desktop `s()` / typography helpers renders 1:1 against the 390-px Figma mobile
 * baseline instead of the global ~0.4 desktop clamp. The panel sizes to its
 * content up to 92 vh — long lists inside should cap their own height and scroll.
 */
@Composable
fun BottomSheet(
    onDismiss: () -> Unit,
    contentGap: Number = 16,
    /** Open at the full allowed height (the capped max-height) instead of
     *  sizing to content. Use for content-heavy sheets that always scroll. */
    fullHeight: Boolean = false,
    /** Gap (px) left below the status bar so the sheet never sits flush against
     *  the very top. Lower it for a taller sheet that rises higher. */
    topGapPx: Number = 60,
    /** Horizontal padding (px) inside the panel. Default 16 matches the page
     *  inset; pass 0 for an edge-to-edge sheet that paints its own background /
     *  full-bleed rows to the screen edges and insets only its inner content. */
    sidePaddingPx: Number = 16,
    /** Restrict swipe-to-dismiss to the handle pill and elements marked with
     *  [SHEET_DRAG_AREA_CLASS] (the sheet's header). Use for form-heavy sheets
     *  where a pull on the content should only ever scroll, never close. */
    dragHeaderOnly: Boolean = false,
    content: @Composable () -> Unit,
) {
    // Shared mutable holder — the scrim's onClick and the panel's touch handlers
    // both trigger the same animated close, wired up once the panel exists.
    val handle = remember { SheetHandle() }

    // Root holds the scrim and panel as SIBLINGS. Fading the scrim's opacity on
    // close must not touch the panel — if the panel were nested inside the scrim,
    // the parent's opacity would drag the panel's var(--l-s2, #09142d) fill to transparent too.
    Div(attrs = {
        style {
            property("position", "fixed")
            // top/left/width/height instead of inset — wireSheetKeyboard
            // re-maps this root onto the VISUAL viewport (height +
            // translateY) on every visualViewport change, so the sheet stays
            // glued to the visible screen no matter how iOS pans/shrinks the
            // viewport for the software keyboard.
            property("top", "0")
            property("left", "0")
            property("width", "100%")
            property("height", "100%")
            property("z-index", "1100")
            property("display", "flex")
            property("align-items", "flex-end")
            property("justify-content", "center")
        }
    }) {
        // Scrim — the only dimmed, fading layer; also the outside-tap target.
        Div(attrs = {
            style {
                property("position", "absolute")
                property("inset", "0")
                property("background", "rgba(2, 4, 11, 0.7)")
                // Start transparent; the ref fades it in on the next frame.
                property("opacity", "0")
                property("transition", "opacity 0.28s ease")
            }
            onClick { handle.close() }
            ref { el ->
                handle.scrim = el
                window.requestAnimationFrame { el.style.opacity = "1" }
                onDispose { handle.scrim = null }
            }
        }) {}

        // Panel — sibling of the scrim, stacked above it.
        Div(attrs = {
            style {
                property("position", "relative")
                property("z-index", "1")
                property("width", "100%")
                property("box-sizing", "border-box")
                property("display", "flex")
                property("flex-direction", "column")
                property("gap", m(contentGap))
                property("--scale", "var(--mobile-scale)")
                // SheetBorderGradient hairline via the border-box trick; the
                // padding-box layer keeps the var(--l-s2, #09142d) fill fully opaque. No
                // bottom border — the sheet is flush with the screen edge, so a
                // 1px hairline there would read as a stray line under the fill.
                property("border-style", "solid")
                property("border-color", "transparent")
                property("border-width", "1px 1px 0 1px")
                property(
                    "background",
                    "linear-gradient(0deg, var(--l-s2, #09142d), var(--l-s2, #09142d)) padding-box, " +
                        "${AppGradients.SheetBorderGradient} border-box"
                )
                property("border-top-left-radius", m(22))
                property("border-top-right-radius", m(22))
                // Always leave a gap below the status bar so tall sheets never
                // sit flush against the very top of the screen. The 92vh line is
                // a fallback for browsers without `dvh` (they drop the invalid
                // calc and keep this).
                property("max-height", "92vh")
                property("max-height", "calc(100dvh - ${topGapPx}px - env(safe-area-inset-top))")
                // Content-heavy sheets open at the full capped height (they
                // scroll anyway), instead of growing from their content size.
                if (fullHeight) {
                    property("height", "92vh")
                    property("height", "calc(100dvh - ${topGapPx}px - env(safe-area-inset-top))")
                }
                // Bottom gap after the last element. Content-sized sheets get a
                // fixed gap here (the panel isn't their scroll container, so this
                // is safe — unlike a scroll container's padding-bottom). Full-
                // height sheets manage their own trailing spacer inside the
                // scroll, so they only get the safe-area inset.
                // The keyboard inset is NOT part of this padding — Safari
                // excludes a scroll container's padding-bottom from the
                // scrollable range, so wireSheetKeyboard adds it as a
                // ::after flex item (sheet-kbd-scroll class + --sheet-kbd
                // var) that scrolls WITH the content instead.
                val bottomPad =
                    if (fullHeight) "env(safe-area-inset-bottom)"
                    else "calc(${m(20)} + env(safe-area-inset-bottom))"
                property("padding", "${m(8)} ${m(sidePaddingPx)} $bottomPad")
                property("box-shadow", "0 -8px 40px rgba(0, 0, 0, 0.45)")
                property("will-change", "transform")
                // Start below the fold; the ref slides it up on the next frame.
                property("transform", "translateY(100%)")
            }
            ref { el ->
                val cleanup = wirePanel(el, handle, dragHeaderOnly, onDismiss)
                val keyboardCleanup = wireSheetKeyboard(el, topGapPx)
                onDispose {
                    cleanup()
                    keyboardCleanup()
                }
            }
        }) {
            // Drag handle — Figma 8425:205911 (36×4 muted pill, centered).
            Div(attrs = {
                classes(SHEET_DRAG_AREA_CLASS)
                style {
                    property("align-self", "center")
                    property("width", m(36))
                    property("height", m(4))
                    property("border-radius", "100px")
                    property("background", "#99b1bf")
                    property("flex-shrink", "0")
                }
            }) {}

            content()
        }
    }
}

/** Mutable bridge between the scrim/panel elements and the active close action. */
private class SheetHandle {
    var scrim: Element? = null
    var close: () -> Unit = {}
}

private const val ENTER_TRANSITION =
    "transform 0.32s cubic-bezier(0.32, 0.72, 0, 1), opacity 0.28s ease"

// Downward travel (px) before a touch counts as a sheet drag rather than a tap.
private const val DRAG_THRESHOLD = 8.0

/** Last measured software-keyboard height (px) — reused as the focus-time
 *  seed so the sheet pads correctly before the keyboard reports itself. */
private var lastSheetKeyboardHeight = 0.0

/** Marks a panel in keyboard-scroll mode: a ::after flex item the size of the
 *  keyboard (--sheet-kbd) is appended INSIDE the scrolled content — Safari
 *  excludes a scroll container's padding-bottom from the scroll range, so a
 *  padding-based inset would not be reachable by scrolling. */
private const val SHEET_KBD_SCROLL_CLASS = "sheet-kbd-scroll"

/** Injects the [SHEET_KBD_SCROLL_CLASS] rule once per document. */
private fun ensureSheetKbdStyle() {
    if (document.getElementById("sheet-kbd-style") != null) return
    val style = document.createElement("style")
    style.id = "sheet-kbd-style"
    style.textContent =
        ".$SHEET_KBD_SCROLL_CLASS::after{content:\"\";display:block;flex-shrink:0;" +
            "height:calc(var(--sheet-kbd, 0px) + env(safe-area-inset-bottom));}"
    document.head?.appendChild(style)
}

/**
 * Keyboard handling for sheets — the sheet must never ride up or float when
 * the software keyboard opens; instead it GROWS to span the visible screen
 * (like the mobile app's sheets).
 *
 *  1. Hard-lock `<html>` + `<body>` with `position: fixed` so iOS WebKit
 *     can't scroll the page when an input inside the sheet gets focus. The
 *     page's scroll offset is preserved via `body { top: -scrollY }` and
 *     restored on cleanup.
 *  2. Follow the visual viewport: on every visualViewport resize/scroll the
 *     sheet's fixed root is re-mapped onto the visible area (height +
 *     translateY(offsetTop)). Whatever WebKit does — pan to reveal the
 *     input, shrink for the keyboard — the sheet stays glued to what the
 *     user actually sees.
 *  3. While the keyboard is up (or a field is focused), the panel is grown to
 *     the root's full capped height, so the sheet runs from the keyboard's
 *     top edge to the usual 60px top gap.
 */
private fun wireSheetKeyboard(panel: HTMLElement, topGapPx: Number): () -> Unit {
    ensureSheetKbdStyle()
    val rootStyle = document.documentElement?.asDynamic()?.style
    val bodyStyle = document.body?.asDynamic()?.style
    val scrollY = window.scrollY

    val prevHtmlPosition = rootStyle?.position as? String ?: ""
    val prevHtmlOverflow = rootStyle?.overflow as? String ?: ""
    val prevHtmlWidth = rootStyle?.width as? String ?: ""
    val prevHtmlHeight = rootStyle?.height as? String ?: ""
    val prevHtmlTop = rootStyle?.top as? String ?: ""
    val prevHtmlLeft = rootStyle?.left as? String ?: ""
    val prevBodyPosition = bodyStyle?.position as? String ?: ""
    val prevBodyOverflow = bodyStyle?.overflow as? String ?: ""
    val prevBodyWidth = bodyStyle?.width as? String ?: ""
    val prevBodyHeight = bodyStyle?.height as? String ?: ""
    val prevBodyTop = bodyStyle?.top as? String ?: ""
    val prevBodyLeft = bodyStyle?.left as? String ?: ""

    // "important" so the lock beats any global CSS rule (Kobweb/Silk sheets).
    rootStyle?.setProperty("position", "fixed", "important")
    rootStyle?.setProperty("overflow", "hidden", "important")
    rootStyle?.setProperty("top", "0", "important")
    rootStyle?.setProperty("left", "0", "important")
    rootStyle?.setProperty("width", "100%", "important")
    rootStyle?.setProperty("height", "100%", "important")
    bodyStyle?.setProperty("position", "fixed", "important")
    bodyStyle?.setProperty("overflow", "hidden", "important")
    bodyStyle?.setProperty("top", "${-scrollY}px", "important")
    bodyStyle?.setProperty("left", "0", "important")
    bodyStyle?.setProperty("width", "100%", "important")
    bodyStyle?.setProperty("height", "100%", "important")

    val vv = window.asDynamic().visualViewport

    // Stable keyboard-free baseline. On iOS `window.innerHeight` itself
    // shrinks with the keyboard (it tracks the visual viewport), so comparing
    // it against visualViewport.height always yields ~0 — the layout
    // viewport's clientHeight is the value that stays put.
    val layoutH: () -> Double = {
        (document.documentElement?.clientHeight ?: window.innerHeight).toDouble()
    }
    // Mobile-app parity: the panel's BOTTOM stays at the real screen bottom
    // (under the keyboard — the layout viewport doesn't shrink on iOS), the
    // panel grows to the full capped height, its content gets a bottom inset
    // the size of the keyboard and becomes scrollable, and the focused field
    // is scrolled into the visible strip above the keyboard.
    val grownHeight = "calc(100% - ${topGapPx}px - env(safe-area-inset-top))"
    val restingHeight = panel.style.getPropertyValue("height")
    var keyboardOpen = false
    // Measured keyboard height; refined by visualViewport, seeded by an
    // estimate at focus time (iOS reports the real value only after the
    // keyboard is already animating in).
    var kbdPx = 0.0
    // Debounce for restoring the resting state after the keyboard closes.
    var shrinkTimer = -1
    val applyPanelState: () -> Unit = {
        // The drag-handle pill stays pinned while the content scrolls under it.
        val pill = panel.firstElementChild?.unsafeCast<HTMLElement>()
        if (keyboardOpen) {
            panel.style.setProperty("height", grownHeight)
            panel.style.setProperty("overflow-y", "auto")
            // Keep panel scrolls INSIDE the panel — without this iOS chains
            // the overscroll into a visual-viewport pan (the whole page
            // drags around while the keyboard is open).
            panel.style.setProperty("overscroll-behavior", "contain")
            panel.style.setProperty("--sheet-kbd", "${kbdPx}px")
            panel.classList.add(SHEET_KBD_SCROLL_CLASS)
            pill?.style?.setProperty("position", "sticky")
            pill?.style?.setProperty("top", "0")
            pill?.style?.setProperty("z-index", "1")
        } else {
            if (restingHeight.isBlank()) panel.style.removeProperty("height")
            else panel.style.setProperty("height", restingHeight)
            panel.style.removeProperty("overflow-y")
            panel.style.removeProperty("overscroll-behavior")
            panel.style.removeProperty("--sheet-kbd")
            panel.classList.remove(SHEET_KBD_SCROLL_CLASS)
            pill?.style?.removeProperty("position")
            pill?.style?.removeProperty("top")
            pill?.style?.removeProperty("z-index")
        }
    }
    val editable: (Element?) -> Boolean = { el ->
        val tag = el?.tagName?.uppercase()
        tag == "INPUT" || tag == "TEXTAREA" ||
            (el?.unsafeCast<HTMLElement>()?.isContentEditable == true)
    }
    // Bring the focused field into the visible strip above the keyboard —
    // scrolls the panel (now a scroll container), NOT the viewport, so WebKit
    // has no reason to pan the page.
    val revealFocused: () -> Unit = {
        val ae = document.activeElement
        if (ae != null && editable(ae) && panel.contains(ae)) {
            ae.asDynamic().scrollIntoView(js("{block: 'center', behavior: 'instant'}"))
        }
    }

    val update: () -> Unit = {
        val vvH: Double = if (vv != null) (vv.height as? Double) ?: layoutH() else layoutH()
        // Keyboard = a large visual-viewport shrink vs the stable layout
        // height (smaller deltas are URL-bar/chrome animations). Never shrink
        // the sheet back while a field is still focused — WKWebView-based
        // browsers (Chrome iOS) fire viewport resizes for browser-chrome
        // animation without reporting the keyboard, and that must not
        // collapse the grown sheet mid-typing.
        val measured = (layoutH() - vvH).coerceAtLeast(0.0)
        val open = measured > 100
        if (open) {
            if (shrinkTimer >= 0) {
                window.clearTimeout(shrinkTimer)
                shrinkTimer = -1
            }
            lastSheetKeyboardHeight = measured
            if (kotlin.math.abs(measured - kbdPx) > 24.0 || !keyboardOpen) {
                kbdPx = measured
                keyboardOpen = true
                applyPanelState()
                revealFocused()
            }
        } else if (keyboardOpen && shrinkTimer < 0) {
            // Keyboard gone (even if the field kept focus — e.g. swiped away):
            // restore the resting sheet after a short debounce that skips the
            // partial values reported while the keyboard is still animating in.
            shrinkTimer = window.setTimeout({
                shrinkTimer = -1
                val vvNow: Double = if (vv != null) (vv.height as? Double) ?: layoutH() else layoutH()
                if (keyboardOpen && (layoutH() - vvNow) < 100) {
                    keyboardOpen = false
                    kbdPx = 0.0
                    applyPanelState()
                }
            }, 300)
        }
        // Undo any scroll WebKit forced on the locked document.
        if (window.scrollY != 0.0) window.scrollTo(0.0, 0.0)
    }
    update()

    // WKWebView-based browsers (Chrome iOS) natively scroll the page to
    // reveal a focused input even when the document is position-locked, and
    // that scroll is only partially visible to JS. Stomp it: on any window
    // scroll, and in a short burst after each focus, force the document back
    // to 0.
    val stompScroll: () -> Unit = {
        if (window.scrollY != 0.0) window.scrollTo(0.0, 0.0)
        val se = document.asDynamic().scrollingElement
        if (se != null && (se.scrollTop as? Double ?: 0.0) != 0.0) se.scrollTop = 0
    }
    var burstTimer = -1
    val startStompBurst: () -> Unit = {
        if (burstTimer >= 0) window.clearInterval(burstTimer)
        var ticks = 0
        burstTimer = window.setInterval({
            stompScroll()
            if (++ticks >= 20 && burstTimer >= 0) {
                window.clearInterval(burstTimer)
                burstTimer = -1
            }
        }, 50)
    }
    val onWindowScroll: (Event) -> Unit = { stompScroll() }
    window.addEventListener("scroll", onWindowScroll)

    // Grow already at focus time — before the keyboard's first resize event —
    // and scroll the field into the safe upper strip, so WebKit has nothing
    // to pan to when the keyboard arrives. iOS only reports the keyboard
    // height once it's animating, so seed with the last measured height (or
    // a 42%-of-screen estimate on the very first focus); the visualViewport
    // handler refines it moments later. On Android (resizes-content) the
    // measured delta stays ~0 and no padding is ever applied — the browser
    // already resizes the layout itself.
    val isIOS = window.navigator.userAgent.let {
        it.contains("iPhone") || it.contains("iPad") || it.contains("iPod")
    }
    val onFocusIn: (Event) -> Unit = { e ->
        if (editable(e.target.unsafeCast<Element?>())) {
            if (!keyboardOpen) {
                keyboardOpen = true
                if (isIOS) {
                    kbdPx = if (lastSheetKeyboardHeight > 100) lastSheetKeyboardHeight
                    else 0.42 * layoutH()
                }
                applyPanelState()
            }
            window.requestAnimationFrame { revealFocused() }
            startStompBurst()
        }
    }
    val onFocusOut: (Event) -> Unit = {
        // Focus may be moving to another field — settle after a beat and only
        // shrink back when nothing editable is focused and no keyboard is up.
        window.setTimeout({
            val vvH: Double = if (vv != null) (vv.height as? Double) ?: layoutH() else layoutH()
            if (keyboardOpen && !editable(document.activeElement) && (layoutH() - vvH) < 100) {
                keyboardOpen = false
                kbdPx = 0.0
                applyPanelState()
            }
        }, 250)
    }
    panel.addEventListener("focusin", onFocusIn)
    panel.addEventListener("focusout", onFocusOut)

    val onChange: (Event) -> Unit = { update() }
    if (vv != null) {
        vv.addEventListener("resize", onChange)
        vv.addEventListener("scroll", onChange)
    }
    window.addEventListener("resize", onChange)

    return {
        panel.removeEventListener("focusin", onFocusIn)
        panel.removeEventListener("focusout", onFocusOut)
        window.removeEventListener("scroll", onWindowScroll)
        if (burstTimer >= 0) window.clearInterval(burstTimer)
        if (shrinkTimer >= 0) window.clearTimeout(shrinkTimer)
        if (vv != null) {
            vv.removeEventListener("resize", onChange)
            vv.removeEventListener("scroll", onChange)
        }
        window.removeEventListener("resize", onChange)
        keyboardOpen = false
        kbdPx = 0.0
        applyPanelState()
        rootStyle?.position = prevHtmlPosition
        rootStyle?.overflow = prevHtmlOverflow
        rootStyle?.top = prevHtmlTop
        rootStyle?.left = prevHtmlLeft
        rootStyle?.width = prevHtmlWidth
        rootStyle?.height = prevHtmlHeight
        bodyStyle?.position = prevBodyPosition
        bodyStyle?.overflow = prevBodyOverflow
        bodyStyle?.top = prevBodyTop
        bodyStyle?.left = prevBodyLeft
        bodyStyle?.width = prevBodyWidth
        bodyStyle?.height = prevBodyHeight
        window.scrollTo(0.0, scrollY)
    }
}

/**
 * Installs the panel's enter animation, swipe-to-dismiss drag and animated
 * close. Returns a cleanup that detaches the touch listeners.
 */
private fun wirePanel(
    panel: HTMLElement,
    handle: SheetHandle,
    dragHeaderOnly: Boolean,
    onDismiss: () -> Unit,
): () -> Unit {
    // Slide up from the start translateY(100%) once laid out.
    window.requestAnimationFrame {
        window.requestAnimationFrame {
            panel.style.transition = ENTER_TRANSITION
            panel.style.transform = "translateY(0)"
        }
    }

    var closing = false
    val animateClose = {
        if (!closing) {
            closing = true
            panel.style.transition = ENTER_TRANSITION
            panel.style.transform = "translateY(100%)"
            (handle.scrim?.unsafeCast<HTMLElement>())?.style?.opacity = "0"
            window.setTimeout({ onDismiss() }, 320)
        }
    }
    handle.close = animateClose

    var startY = 0.0
    var dy = 0.0
    var dragging = false  // gesture candidate (not on a control / scrolled list)
    var armed = false     // moved past the threshold → actually dragging the sheet
    // Keyboard-mode manual scroll: while wireSheetKeyboard made the panel the
    // scroll container, touch moves are handled entirely by hand
    // (preventDefault + scrollTop) so the browser can never turn the gesture
    // into a page/visual-viewport pan or WKWebView's native inset scroll.
    var kbScrolling = false
    var kbLastY = 0.0

    val tsHandler: (Event) -> Unit = { e ->
        if (!closing) {
            val t = e.asDynamic().touches[0]
            startY = (t.clientY as Number).toDouble()
            dy = 0.0
            armed = false
            val kbScrollMode = panel.style.getPropertyValue("overflow-y") == "auto"
            val target = e.target.unsafeCast<Element?>()
            if (kbScrollMode) {
                // The handle band (top 44px of the panel — the sticky pill
                // strip) or a marked header still drags the sheet closed even
                // with the keyboard up; content pulls scroll the panel by
                // hand; a touch over an inner scrollable list keeps its own
                // native scrolling.
                val headerDrag = (startY - panel.getBoundingClientRect().top) < 44.0 ||
                    (target != null && target != panel && inDragArea(target, panel))
                if (headerDrag) {
                    dragging = true
                    kbScrolling = false
                    e.stopPropagation()
                } else {
                    dragging = false
                    kbScrolling = !hasScrollableBetween(target, panel)
                    kbLastY = startY
                    if (kbScrolling) e.stopPropagation()
                }
            } else {
                kbScrolling = false
                // Grab the sheet only when the touch isn't on a form control / map and
                // no scrollable ancestor is scrolled. Skipping controls is what lets an
                // input focus / the keyboard open — see the threshold note below.
                // With dragHeaderOnly, only the handle pill / marked header can start
                // the gesture at all — content pulls always stay with the content.
                dragging = !isInteractive(target, panel) &&
                    (if (dragHeaderOnly) inDragArea(target, panel) else !scrollLocked(target, panel))
                // Isolate this sheet's gesture from any ancestor sheet's panel listeners
                // (sheet-in-sheet) ONLY when we're actually taking the drag. When we're
                // not (a control, a map, a scrolled list), let the event bubble so e.g.
                // Leaflet's document-level pan handler still receives it.
                if (dragging) e.stopPropagation()
            }
        }
    }
    val tmHandler: (Event) -> Unit = { e ->
        if (kbScrolling && !closing) {
            e.preventDefault()
            e.stopPropagation()
            val t = e.asDynamic().touches[0]
            val y = (t.clientY as Number).toDouble()
            panel.scrollTop = panel.scrollTop + (kbLastY - y)
            kbLastY = y
        } else if (dragging && !closing) {
            e.stopPropagation()
            val t = e.asDynamic().touches[0]
            val delta = (t.clientY as Number).toDouble() - startY
            // Don't engage (or preventDefault) until the finger has clearly moved
            // down. A tap carries 1–2 px of jitter; preventDefault on that would
            // cancel the click that focuses an input → keyboard never opens.
            if (!armed) {
                if (delta > DRAG_THRESHOLD) {
                    armed = true
                    panel.style.transition = "none"
                }
            }
            if (armed) {
                if (delta > 0) {
                    dy = delta
                    e.preventDefault()
                    panel.style.transform = "translateY(${delta}px)"
                    val h = panel.offsetHeight.toDouble().coerceAtLeast(1.0)
                    val ratio = (1.0 - delta / h).coerceIn(0.0, 1.0)
                    (handle.scrim?.unsafeCast<HTMLElement>())?.style?.opacity =
                        (0.15 + 0.85 * ratio).toString()
                } else {
                    dy = 0.0
                    panel.style.transform = "translateY(0)"
                    (handle.scrim?.unsafeCast<HTMLElement>())?.style?.opacity = "1"
                }
            }
        }
    }
    val teHandler: (Event) -> Unit = { e ->
        if (kbScrolling) {
            kbScrolling = false
            e.stopPropagation()
        }
        if (dragging) e.stopPropagation()
        // Only resolve a gesture that actually engaged — a bare tap (never armed)
        // falls through so its click/focus proceeds untouched.
        if (dragging && armed) {
            val h = panel.offsetHeight.toDouble().coerceAtLeast(1.0)
            // Past ~28 % of the sheet height (or 110 px) → dismiss; else spring back.
            if (dy > 110.0 || dy > h * 0.28) {
                animateClose()
            } else {
                panel.style.transition = ENTER_TRANSITION
                panel.style.transform = "translateY(0)"
                (handle.scrim?.unsafeCast<HTMLElement>())?.style?.opacity = "1"
            }
        }
        dragging = false
        armed = false
    }

    panel.addEventListener("touchstart", tsHandler)
    panel.addEventListener("touchmove", tmHandler)
    panel.addEventListener("touchend", teHandler)
    panel.addEventListener("touchcancel", teHandler)

    return {
        panel.removeEventListener("touchstart", tsHandler)
        panel.removeEventListener("touchmove", tmHandler)
        panel.removeEventListener("touchend", teHandler)
        panel.removeEventListener("touchcancel", teHandler)
    }
}

/**
 * True when any element between [start] and [stop] (exclusive) is itself a
 * vertical scroller with real overflow — the touch should stay with that
 * element's own native scrolling.
 */
private fun hasScrollableBetween(start: Element?, stop: Element): Boolean {
    var node: Element? = start
    while (node != null && node != stop) {
        val el = node.unsafeCast<HTMLElement>()
        val cs = window.getComputedStyle(el)
        val overflowY = cs.getPropertyValue("overflow-y")
        if ((overflowY == "auto" || overflowY == "scroll") &&
            el.scrollHeight > el.clientHeight + 1
        ) {
            return true
        }
        node = el.parentElement
    }
    return false
}

/**
 * True when any ancestor between [start] and [stop] is itself a vertical
 * scroller that isn't pinned to the top — i.e. the touch should scroll that
 * element, not drag the sheet.
 */
private fun scrollLocked(start: Element?, stop: Element): Boolean {
    var node: Element? = start
    while (node != null && node != stop) {
        val el = node.unsafeCast<HTMLElement>()
        val cs = window.getComputedStyle(el)
        val overflowY = cs.getPropertyValue("overflow-y")
        if (overflowY == "auto" || overflowY == "scroll") {
            val canScroll = el.scrollHeight > el.clientHeight + 1
            if (canScroll) {
                // `column-reverse` scrollers (the chat message list) rest at
                // scrollTop ≈ 0 (newest) and go NEGATIVE as you scroll into
                // history, so the `scrollTop > 0` test never fires and the
                // sheet's drag-to-dismiss would hijack the chat scroll. Treat
                // any scrollable reverse list as owning the gesture.
                val reverse = cs.getPropertyValue("flex-direction") == "column-reverse"
                if (reverse) return true
                if (el.scrollTop > 0.0) return true
            }
        }
        node = el.parentElement
    }
    return false
}

/**
 * True when the touch landed on (or inside) an element carrying
 * [SHEET_DRAG_AREA_CLASS] — the built-in handle pill or a header the caller
 * marked — within this panel. The bare panel itself also counts: its padding
 * and the flex gaps around the pill/header are sheet chrome, not content, so
 * the whole band from the top edge down to the header stays draggable.
 */
private fun inDragArea(start: Element?, stop: Element): Boolean {
    if (start == stop) return true
    var node: Element? = start
    while (node != null && node != stop) {
        if (node.classList.contains(SHEET_DRAG_AREA_CLASS)) return true
        node = node.parentElement
    }
    return false
}

/**
 * True when the touch landed on (or inside) a form control — an input, textarea,
 * select, button or contenteditable — or an interactive map. The sheet must not
 * hijack those gestures, otherwise the tap never becomes a focus/click (keyboard
 * won't open) or the map can't be panned.
 */
private fun isInteractive(start: Element?, stop: Element): Boolean {
    var node: Element? = start
    while (node != null && node != stop) {
        when (node.tagName.uppercase()) {
            "INPUT", "TEXTAREA", "SELECT", "BUTTON" -> return true
        }
        if (node.unsafeCast<HTMLElement>().isContentEditable) return true
        // A Leaflet map handles its own pan/zoom gestures.
        if (node.getAttribute("class")?.contains("leaflet") == true) return true
        node = node.parentElement
    }
    return false
}
